import { yupResolver } from "@hookform/resolvers/yup"
import type { E164Number } from "libphonenumber-js/types"
import { type FC, useContext } from "react"
import type { FieldErrors } from "react-hook-form"
import { useForm } from "react-hook-form"
import { type Country, formatPhoneNumberIntl } from "react-phone-number-input/input"
import * as yup from "yup"

import type { BaseUserFragment } from "../../generated/graphql"
import { AppContext } from "../../providers/AppProvider"
import { phoneSubmitErrorMessage } from "../../utils/errors"
import { phoneNumberField } from "../../utils/phone"
import { useCommunityCustomization } from "../../utils/useCommunityCustomization"
import useCountyCodeByIP from "../../utils/useCountyCodeByIP"
import { usePhoneAuth } from "../../utils/usePhoneAuth"
import VerifyCode from "../auth-checkout/VerifyCode"
import { ModalBase } from "../ModalBase"
import { PhoneInputField } from "../PhoneInputField"
import UbiqButton, { UbiqButtonSize, UbiqButtonType } from "../UbiqButton"

const AUTH_MODAL_PANEL_STYLES =
  "w-[calc(100vw-32px)] pt-12 px-6 pb-10 font-mono sm:max-w-[480px] sm:px-[54px] flex flex-col items-center"

const phoneSchema = yup.object().shape({
  phone: phoneNumberField(),
})

interface PhoneFormValues {
  phone: E164Number
}

type PhoneInputErrors = FieldErrors<{ phone: string }>

interface AuthProps {
  onAuthSuccess: (params: {
    accessToken: string
    user: BaseUserFragment | undefined
  }) => Promise<void>
  onBack: () => void
  modalViewportStyles: string
  modalContainerStyles: string
  modalPanelStyles: string
}

export const Auth: FC<AuthProps> = ({
  onAuthSuccess,
  onBack,
  modalViewportStyles,
  modalContainerStyles,
  modalPanelStyles,
}) => {
  const customizationData = useCommunityCustomization()
  const { showToSModal, showPPModal } = useContext(AppContext)
  const countryCode = useCountyCodeByIP()
  const phoneAuth = usePhoneAuth()

  const phoneForm = useForm<PhoneFormValues>({
    criteriaMode: "all",
    defaultValues: { phone: "" as E164Number },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: yupResolver(phoneSchema),
  })

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = phoneForm

  const handlePhoneSubmit = async (data: PhoneFormValues) => {
    if (!data.phone) return

    try {
      await Promise.all([
        phoneAuth.openOtpAfterKeyboardDismiss(data.phone),
        phoneAuth.submitPhone(data.phone),
      ])
    } catch (err) {
      phoneAuth.closeOtp()
      setError("phone", { message: phoneSubmitErrorMessage(err) })
    }
  }

  const handleOtpVerify = async (code: string) => {
    const result = await phoneAuth.verifyOtp(code)
    await onAuthSuccess({
      accessToken: result.token,
      user: result.user,
    })
  }

  if (!customizationData) return null
  const modalWindowStyles = customizationData.styles.global.modalWindow

  return (
    <>
      <ModalBase
        dialogContainerStyles={modalContainerStyles}
        dialogPanelStyles={`${AUTH_MODAL_PANEL_STYLES} ${modalWindowStyles.container} ${modalPanelStyles}`}
        dialogViewportStyles={modalViewportStyles}
        show={!phoneAuth.showOtpModal}
      >
        <h2 className={`text-primary-h2 text-center mb-4 ${modalWindowStyles.title}`}>
          Log in / Sign up
        </h2>
        <p className={`text-body-lg text-center mb-6 ${modalWindowStyles.subtitle}`}>
          Please enter your phone number.
        </p>

        <form
          className="w-full flex flex-col items-center gap-6"
          onSubmit={(e) => void handleSubmit(handlePhoneSubmit)(e)}
        >
          <div className="w-full relative max-w-[256px]">
            <PhoneInputField
              className="w-full"
              clearErrors={() => clearErrors("phone")}
              control={control}
              countryCode={countryCode as Country}
              errors={errors as unknown as PhoneInputErrors}
            />
            {errors.phone && (
              <p className="first-letter:uppercase absolute top-full text-body-sm text-error text-center w-full">
                {errors.phone.message}
              </p>
            )}
          </div>

          <UbiqButton
            buttonType={UbiqButtonType.LIGHT}
            disabled={isSubmitting}
            size={UbiqButtonSize.SMALL}
            title={isSubmitting ? "SENDING..." : "SEND CODE"}
            type="submit"
          />

          <p className="text-body-md text-center text-textPrimary font-mono sm:px-8">
            By signing up, you create an account on WERZ and agree to its{" "}
            <button
              className="cursor-pointer underline appearance-none border-0 bg-transparent p-0"
              onClick={() => showToSModal()}
              type="button"
            >
              Terms
            </button>
            {" and "}
            <button
              className="cursor-pointer underline appearance-none border-0 bg-transparent p-0"
              onClick={() => showPPModal()}
              type="button"
            >
              Privacy Policy
            </button>
            .
          </p>
        </form>

        <div className="mt-6 w-full text-center">
          <button
            className="mx-auto block cursor-pointer text-center font-mono text-primary-h5 font-bold underline appearance-none border-0 bg-transparent p-0"
            onClick={onBack}
            type="button"
          >
            GO BACK
          </button>
        </div>
      </ModalBase>

      <VerifyCode
        codeLength={4}
        description={`Enter the code sent to\n${formatPhoneNumberIntl(phoneAuth.phoneNumber)}.`}
        error={phoneAuth.otpError}
        isPhoneVerification
        isSubmitting={phoneAuth.isOtpVerifying}
        onClose={phoneAuth.closeOtp}
        onConfirm={handleOtpVerify}
        onResend={phoneAuth.resendOtp}
        show={phoneAuth.showOtpModal}
        title="Verify phone number"
      />
    </>
  )
}
